import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Star, GripVertical } from "lucide-react";
import { api, formatApiError } from "../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const photoUrl = (id) => `${BACKEND}/api/rooms/photos/${id}`;

const ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
const MAX = 5;

export function RoomPhotos({ room, onClose, onChange, canEdit = true }) {
  const [photos, setPhotos] = useState(room.photos || []);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const inputRef = useRef(null);

  const sync = (list) => {
    setPhotos(list);
    onChange(room.id, list);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    if (photos.length + files.length > MAX) {
      toast.error(`Máximo ${MAX} fotografías por habitación.`);
      return;
    }
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/rooms/${room.id}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      sync(data.photos);
      toast.success("Fotografías subidas");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (photoId) => {
    try {
      const { data } = await api.delete(`/admin/rooms/${room.id}/photos/${photoId}`);
      sync(data.photos);
      toast.success("Fotografía eliminada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const persistOrder = async (list) => {
    try {
      const { data } = await api.put(`/admin/rooms/${room.id}/photos/order`, {
        photo_ids: list.map((p) => p.id),
      });
      sync(data.photos);
    } catch (err) {
      toast.error("No se pudo reordenar");
    }
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const list = [...photos];
    const from = list.findIndex((p) => p.id === dragId);
    const to = list.findIndex((p) => p.id === targetId);
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setPhotos(list);
    persistOrder(list);
    setDragId(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="room-photos-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full p-8 overflow-y-auto">
        <button onClick={onClose} className="absolute top-5 right-5 text-black/40 hover:text-black">
          <X size={22} />
        </button>
        <h3 className="text-xl font-bold text-black">Fotos · Habitación {room.number}</h3>
        <p className="mt-1 text-sm font-light text-black/50">
          {canEdit
            ? `Sube de 3 a ${MAX} fotografías (JPG, JPEG, PNG o WEBP). La primera es la foto principal.`
            : "Vista de solo lectura. Tu rol no permite modificar las fotografías."}
        </p>

        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              data-testid="photo-file-input"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading || photos.length >= MAX}
              data-testid="upload-photos-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-black transition-colors disabled:opacity-50"
            >
              <Upload size={16} /> {uploading ? "Subiendo..." : "Subir fotos"}
            </button>
            <span className="ml-3 text-xs font-light text-[#B2967D]">{photos.length}/{MAX}</span>
          </>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4">
          {photos.map((p, i) => (
            <div
              key={p.id}
              draggable={canEdit}
              onDragStart={canEdit ? () => setDragId(p.id) : undefined}
              onDragOver={canEdit ? (e) => e.preventDefault() : undefined}
              onDrop={canEdit ? () => onDrop(p.id) : undefined}
              data-testid={`photo-thumb-${i}`}
              className={`relative group border ${i === 0 ? "border-[#7D5A44]" : "border-[#B2967D]/30"} ${canEdit ? "cursor-move" : ""}`}
            >
              <img src={photoUrl(p.id)} alt={`Foto ${i + 1}`} className="w-full h-32 object-cover" />
              {i === 0 && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#7D5A44] text-white text-[9px] font-bold uppercase tracking-wide px-2 py-1">
                  <Star size={10} /> Principal
                </span>
              )}
              {canEdit && (
                <>
                  <span className="absolute top-2 right-2 text-white/80 bg-black/30 p-1">
                    <GripVertical size={12} />
                  </span>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`photo-delete-${i}`}
                    className="absolute bottom-2 right-2 bg-white/90 text-red-600 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </>
              )}
            </div>
          ))}
          {photos.length === 0 && (
            <div className="col-span-2 text-center py-10 text-sm font-light text-black/40 border border-dashed border-[#B2967D]/40">
              {canEdit ? "Aún no hay fotografías. Sube al menos 3." : "Esta habitación no tiene fotografías."}
            </div>
          )}
        </div>

        {canEdit && photos.length > 0 && photos.length < 3 && (
          <p className="mt-4 text-xs text-amber-600 font-light">
            Recomendado: sube al menos 3 fotografías para esta habitación.
          </p>
        )}
        {canEdit && (
          <p className="mt-4 text-xs font-light text-[#B2967D]">
            Arrastra las miniaturas para reordenarlas. La primera será la foto principal en el sitio.
          </p>
        )}
      </div>
    </div>
  );
}
